/** Money columns on the summary CSV `Totals` row (R-APP-17). */
export interface NormalisationSummaryTotals {
  grossPay: string;
  gross: string;
  tax: string;
  nettPay: string;
  studentLoan: string;
  ksEe: string;
  ksEr: string;
  esct: string;
  otherSuperEe: string;
  otherSuperEr: string;
  totalDeductions: string;
}

export interface NormalisationDetailPreviewRow {
  /** Payslip block number in the combined PDF (1-based when provided by the API). */
  payslipBlockIndex?: number;
  employeeCode: string;
  taxCode: string;
  grossPay: string;
  gross: string;
  tax: string;
  nettPay: string;
  studentLoan: string;
  ksEe: string;
  ksEr: string;
  esct: string;
  otherSuperEe: string;
  otherSuperEr: string;
}

export interface NormalisationIssue {
  code: string;
  /** Wireframe cluster headline when distinct from `code`. */
  title?: string;
  message: string;
  employeeCode?: string;
  payslipBlockIndex?: number;
}

export type NormalisationArtifactKind =
  | "detail"
  | "summary"
  | "super"
  | "issues"
  | "bundle";

export interface NormalisationRunSuccess {
  outcome: "complete" | "partial";
  runId: string;
  /** Catalogue id from the normalisation run (e.g. ``MYOB IMS``). */
  importSourceId: string;
  parserProfileId: string;
  payslipBlocksTotal: number;
  payslipBlocksParsed: number;
  payslipBlocksFailed: number;
  /** Employees represented in summary.csv totals (successfully parsed payslip blocks). */
  employeeSummaryRows: number;
  warningCount: number;
  summaryTotals: NormalisationSummaryTotals;
  detailPreview: NormalisationDetailPreviewRow[];
  /** Artifact keys the run exposes for download (`detail.csv`, `summary.csv`, …). */
  availableArtifacts: readonly string[];
  /** Present when `outcome` is `partial` (CN-08). */
  issues?: NormalisationIssue[];
}

export interface NormalisationRunBlocked {
  outcome: "blocked";
  runId: string;
  importSourceId: string;
  parserProfileId: string;
  issues: NormalisationIssue[];
}

export type NormalisationRunResult =
  | NormalisationRunSuccess
  | NormalisationRunBlocked;

export interface ClientPayNormalisationInput {
  importSourceId: string;
  file: File;
  payPeriodLabel?: string;
}

export interface NormalisationArtifactRequest {
  runId: string;
  kind: NormalisationArtifactKind;
  /** Original payslip PDF name from the normalise request (download naming). */
  importFileName: string;
}

export function isNormalisationRunBlocked(
  result: NormalisationRunResult,
): result is NormalisationRunBlocked {
  return result.outcome === "blocked";
}

export function isNormalisationRunSuccess(
  result: NormalisationRunResult,
): result is NormalisationRunSuccess {
  return result.outcome === "complete" || result.outcome === "partial";
}
